import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { RollResult } from '../lib/dice'
import { savePreferences, loadPreferences } from '@/shared/api'
import { useAuthStore } from '@/shared/session'

/** Longest the "animate" slider may request (its ceiling). `0` means Instant. */
export const MAX_ROLL_ANIMATION_MS = 3000

/** Lifecycle of the active roll shown in the dice tray. */
export type RollStatus = 'idle' | 'rolling' | 'settled'

interface RollStore {
  /**
   * Animation preference. `0` = Instant (skip the 3D tumble, show the result
   * immediately); any value > 0 = play the physics roll. Persisted.
   */
  durationMs: number
  setDurationMs: (ms: number) => void

  /** Dice texture key (e.g. `"marble"`). Persisted. */
  diceTexture: string
  setDiceTexture: (texture: string) => void

  /** Dye colour painted over the texture (hex, e.g. `"#ffffff"` = none). Persisted. */
  diceColor: string
  setDiceColor: (color: string) => void

  /** Number/pip colour: a hex string or `"auto"`. Persisted. */
  diceFontColor: string
  setDiceFontColor: (color: string) => void

  /** Whether roll sounds are muted. Persisted; toggled from the roll window or settings. */
  muted: boolean
  setMuted: (muted: boolean) => void

  /** Load persisted roll prefs from the cloud. Called on sign-in. */
  loadRollPrefsFromCloud: () => Promise<void>

  // transient roll state (not persisted)
  status: RollStatus
  /** The settled result (RNG-chosen values); the tray only choreographs to it. */
  result: RollResult | null
  /** Most-recent-first log of settled results. */
  history: RollResult[]

  startRolling: () => void
  settle: (result: RollResult) => void
  resetRoll: () => void
}

// Shares the debounce and the create-or-patch dance with `settingsStore` via
// `savePreferences`: the two write disjoint halves of one record.
function scheduleRollPrefSync() {
  savePreferences(useAuthStore.getState().user?.id, 'roll_settings', () => {
    const s = useRollStore.getState()
    return {
      durationMs: s.durationMs,
      diceTexture: s.diceTexture,
      diceColor: s.diceColor,
      diceFontColor: s.diceFontColor,
      muted: s.muted,
    }
  })
}

export const useRollStore = create<RollStore>()(
  persist(
    (set) => ({
      durationMs: 800,
      setDurationMs: (ms) => {
        set({ durationMs: Math.min(MAX_ROLL_ANIMATION_MS, Math.max(0, Math.round(ms))) })
        scheduleRollPrefSync()
      },

      diceTexture: 'marble',
      setDiceTexture: (texture) => { set({ diceTexture: texture }); scheduleRollPrefSync() },

      diceColor: '#dcd6c8',
      setDiceColor: (color) => { set({ diceColor: color }); scheduleRollPrefSync() },

      diceFontColor: 'auto',
      setDiceFontColor: (color) => { set({ diceFontColor: color }); scheduleRollPrefSync() },

      muted: false,
      setMuted: (muted) => { set({ muted }); scheduleRollPrefSync() },

      loadRollPrefsFromCloud: async () => {
        const p = await loadPreferences<Partial<{
          durationMs: number
          diceTexture: string
          diceColor: string
          diceFontColor: string
          muted: boolean
        }>>(useAuthStore.getState().user?.id, 'roll_settings')
        if (!p) return
        set({
          ...(p.durationMs !== undefined && { durationMs: p.durationMs }),
          ...(p.diceTexture !== undefined && { diceTexture: p.diceTexture }),
          ...(p.diceColor !== undefined && { diceColor: p.diceColor }),
          ...(p.diceFontColor !== undefined && { diceFontColor: p.diceFontColor }),
          ...(p.muted !== undefined && { muted: p.muted }),
        })
      },

      status: 'idle',
      result: null,
      history: [],

      startRolling: () => set({ status: 'rolling' }),
      settle: (result) =>
        set((s) => ({ status: 'settled', result, history: [result, ...s.history].slice(0, 50) })),
      resetRoll: () => set({ status: 'idle', result: null }),
    }),
    {
      name: 'dnd-roll-settings',
      storage: createJSONStorage(() => localStorage),
      // Only the user preferences persist; roll state is per-session.
      partialize: (s) => ({
        durationMs: s.durationMs,
        diceTexture: s.diceTexture,
        diceColor: s.diceColor,
        diceFontColor: s.diceFontColor,
        muted: s.muted,
      }),
    },
  ),
)
